"use client";

import { SubscriptionStatus } from "@prisma/client";
import React from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import IconZap from "@/app/(components)/generics/Icons/IconZap";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useFetchSubscription } from "@/app/hooks/fetchs/credits/useFetchSubscription";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UWallet } from "@/app/utils/UWallet";
import { Limits } from "@/app/vars/Limits";
import sidebarStyles from "../../Sidebar.module.css";
import { PLAN_STATUS_LABEL } from "./SidebarPlan.logic";
import styles from "./SidebarPlan.module.css";

type Props = DefaultProps & {
  isOpen: boolean;
};

export default function SidebarPlan(props: Props) {
  const T = useT();
  const { params, isOpen } = props;

  const user = useAuthUser();
  const { data: overview } = useFetchSubscription({ enabled: !!user });

  const status = overview?.subscription?.status;
  const hasPlan = status === SubscriptionStatus.TRIALING || status === SubscriptionStatus.ACTIVE;

  const hours = user ? UWallet.spendableHours(user, hasPlan) : 0;
  const prospects = user ? UWallet.spendableProspects(user, hasPlan) : 0;

  const planName = status ? T(PLAN_STATUS_LABEL[status]) : T(TKey.PLAN_NAME_NONE);
  const manageLabel = hasPlan ? T(TKey.PLAN_MANAGE) : T(TKey.PLAN_SEE_PLANS);

  const summary = T(TKey.CREDITS_WALLET_TOOLTIP)
    .replace("{{hours}}", String(hours))
    .replace("{{prospects}}", String(prospects));

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    ModalStore.getState().openBilling(params);
  };

  if (!isOpen) {
    return (
      <Tooltip
        position={TooltipPosition.RIGHT}
        gap={Limits.SIDEBAR_TOOLTIP_GAP}
        type={TooltipType.GREEN}
        content={summary}
        className={sidebarStyles.fullWidth}
        trigger={
          <div className={styles.collapsed}>
            <Button
              min={false}
              params={params}
              isLink={false}
              type={ButtonType.GHOST}
              iconOnly={true}
              full={false}
              beforeIcon={<IconZap />}
              className={sidebarStyles.navButton}
              ariaLabel={summary}
              onClick={openModal}
            />
          </div>
        }
      />
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <h3 className={styles.title}>{T(TKey.PLAN_LABEL)}</h3>
        <div className={styles.amountRow}>
          <IconZap size={16} className={styles.icon} />
          <strong className={styles.amount}>{planName}</strong>
        </div>
      </div>
      <button
        type="button"
        className={styles.actionBtn}
        aria-label={manageLabel}
        onClick={openModal}
      >
        <IconArrowRight size={14} />
      </button>
    </div>
  );
}
